import type { EndpointBuilder } from "@reduxjs/toolkit/dist/query/endpointDefinitions";
import type {
    ProjectGasResults,
    ProjectTotalResults,
    ProjectPayload,
    FastProjectsResponse,
    SOCRefProps,
    SOCRefResponse,
    UpdateProjectPayload,
    ProjectUser,
    GetProjectsPayload,
    ProjectAttachment,
    UploadAttachmentPayload,
    GetProjectResultsPayload,
    DownloadProjectReportPayload,
    ProjectLockInfo,
    ImportProjectPayload,
    ImportProjectResponse,
} from "./projectTypes";
import type { AsyncJob, CopyAsyncResponse, ReportAsyncPayload, ReportAsyncResponse } from "../asyncJobs/asyncJobsTypes";
import type { Option } from "@typedefs/modulesInterfaces";
import type { TagTypes } from "../../apiSlice";
import type { ApiSliceType, PublicApiSliceType, Project } from "@typedefs/interfaces";

// Define separate tag types for fast and detailed views
const projectTags = {
    FAST_LIST: 'FastProjects' as TagTypes,
    FAST_DETAIL: (id: number) => ({ type: 'FastProjects' as TagTypes, id }),
    DETAIL_LIST: 'DetailedProjects' as TagTypes,
    DETAIL: (id: number) => ({ type: 'DetailedProjects' as TagTypes, id })
};

interface MoveToTrashbinPayload {
    id: number;
    restore?: boolean;
}

const INCLUDED_NARRATIVE_LANGUAGES = ['en', 'es', 'fr'];

export function createProjectApi(apiSlice: ApiSliceType | PublicApiSliceType) {
    const endpoints = (builder: EndpointBuilder<any, TagTypes, string>) => ({
        // Fast summary query
        getProjects: builder.query<FastProjectsResponse, GetProjectsPayload>({
            query: ({ url, fast, showFinalized, tags, showTrashbin }: GetProjectsPayload) => {
                const params = new URLSearchParams();
                if (fast) params.append("summary", "true");
                if (tags) params.append("tags", tags.join(","));
                params.append("is_archived", showTrashbin ? "true" : "false");
                params.append("is_finalized", showFinalized ? "true" : "false");

                return `${url ? `${url}&` : "projects/?"}${params.toString()}`
            },
            providesTags: (results: FastProjectsResponse | undefined, _error, _arg) => [
                projectTags.FAST_LIST,
                ...(results?.results.map(project => projectTags.FAST_DETAIL(project.id)) ?? []).map(tag => tag)
            ],
        }),

        // Detailed project query
        getProjectById: builder.query<Project, string>({
            query: (id: string) => `projects/${id}/`,
            providesTags: (result: Project | undefined) => result
                ? [
                    projectTags.DETAIL(result.id),
                    projectTags.FAST_DETAIL(result.id)
                ]
                : [],
        }),

        getProjectResults: builder.query<ProjectTotalResults, GetProjectResultsPayload>({
            query: ({ id, activities }: GetProjectResultsPayload) => {
                if (!id || id === 0) {
                    throw new Error('Invalid project ID');
                }
                return activities
                    ? `projects/${id}/results/?activities=${activities?.join(",")}`
                    : `projects/${id}/results/`;
            },
            providesTags: (_result, _error, { id }: GetProjectResultsPayload) => [
                { type: "ProjectResults" as TagTypes, id },
            ],
        }),

        getProjectResultsByGas: builder.query<ProjectGasResults, GetProjectResultsPayload>({
            query: ({ id, activities }: GetProjectResultsPayload) => {
                if (!id || id === 0) {
                    throw new Error('Invalid project ID');
                }
                return activities
                    ? `projects/${id}/results/?activities=${activities?.join(",")}&aggregate=gas`
                    : `projects/${id}/results/?aggregate=gas`;
            },
            providesTags: (_result, _error, { id }: GetProjectResultsPayload) => [
                { type: "ProjectResults" as TagTypes, id },
            ],
        }),

        getSOCref: builder.query<SOCRefResponse | null, void | SOCRefProps>({
            query: (props?: SOCRefProps) => {
                if (!props) return "ipcc/soil-organic-carbons/";
                const { climate, moisture, soil_type } = props;
                const climateStr = climate ? `climate=${climate}&` : "";
                const moistureStr = moisture ? `moisture=${moisture}&` : "";
                const soil_typeStr = soil_type ? `soil_type=${soil_type}` : "";
                return `ipcc/soil-organic-carbons/?${climateStr}${moistureStr}${soil_typeStr}`;
            },
            transformResponse: (response: SOCRefResponse[]) =>
                response?.length ? response[0] : null,
        }),

        getProjectMemberships: builder.query<ProjectUser[], number>({
            query: (id: number) => `projects/${id}/memberships/`,
            providesTags: ['ProjectUsers' as TagTypes],
        }),

        getMyMemberships: builder.query<ProjectUser[], { projectId: number, userId: number }>({
            query: ({ projectId, userId }: { projectId: number, userId: number }) => `projects/${projectId}/memberships/?user=${userId}`,
            transformResponse: (response: ProjectUser[], _meta: any, { userId }: { projectId: number, userId: number }) => {
                return response.filter((member) => member.user.id === userId)
            },
            providesTags: (result, _error, { projectId, userId }: { projectId: number, userId: number }) => result ? [{ type: 'ProjectUsers' as TagTypes, id: `${projectId}-${userId}` }] : [],
        }),

        getProjectAttachments: builder.query<ProjectAttachment[], number>({
            query: (projectId: number) => `project-attachments/?project_id=${projectId}`,
            providesTags: (result) =>
                result
                    ? [...result.map(({ id }) => ({ type: 'ProjectAttachments' as TagTypes, id })), { type: 'ProjectAttachments' as TagTypes, id: 'LIST' }]
                    : [{ type: 'ProjectAttachments' as TagTypes, id: 'LIST' }],
        }),

        downloadProjectAttachment: builder.query<Blob, number>({
            query: (id: number) => ({
                url: `project-attachments/${id}/download/`,
                method: 'GET',
                responseHandler: (response: Response) => response.blob(),
            }),
        }),

        downloadProjectReport: builder.query<Blob, DownloadProjectReportPayload>({
            query: ({ id, activities, template, currentLanguage }: DownloadProjectReportPayload) => {
                const params = new URLSearchParams();

                if (activities) params.append('activities', activities.join(","));
                if (template) params.append('template', template);

                const currentLangIsIncluded = INCLUDED_NARRATIVE_LANGUAGES.includes(currentLanguage);
                const acceptLanguage = currentLangIsIncluded ? currentLanguage : 'en';

                return {
                    url: `projects/${id}/report/?${params.toString()}`,
                    method: 'GET',
                    responseHandler: (response: Response) => response.blob(),
                    headers: acceptLanguage ? {
                        'Accept-Language': acceptLanguage,
                    } : {}
                }
            },
            async onQueryStarted(_, { queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch (error: any) {
                    if (error?.data instanceof Blob) {
                        const text = await error.data.text();
                        try {
                            const json = JSON.parse(text);
                            error.data = json;
                        } catch {
                            error.data = { message: 'Error downloading report' };
                        }
                    }
                }
            }
        }),

        getAllTags: builder.query<Option[], void>({
            query: () => "projects/tags/",
            providesTags: ['Tags' as TagTypes],
        }),

        getTagsByProject: builder.query<Option[], number>({
            query: (projectId: number) => `projects/${projectId}/tags/`,
            providesTags: (result, _error, projectId: number) =>
                result
                    ? [
                        ...result.map(tag => ({ type: 'ProjectTags' as TagTypes, id: tag.id })),
                        { type: 'ProjectTags' as TagTypes, id: projectId }
                    ]
                    : [{ type: 'ProjectTags' as TagTypes, id: projectId }]
        }),

        getProjectLockInfo: builder.query<ProjectLockInfo, string>({
            query: (projectId: string) => `projects/${projectId}/lock/`,
        }),

        //MUTATIONS!
        createProject: builder.mutation<Project, ProjectPayload>({
            query: (data: ProjectPayload) => ({
                url: "projects/",
                method: "POST",
                body: data,
            }),
            invalidatesTags: [
                projectTags.FAST_LIST,
                projectTags.DETAIL_LIST
            ],
        }),

        updateProject: builder.mutation<Project, UpdateProjectPayload>({
            query: ({ id, body }: UpdateProjectPayload) => ({
                url: `projects/${id}/`,
                method: "PATCH",
                body: { ...body, id },
            }),
            invalidatesTags: (_results, _error, { id }: UpdateProjectPayload) => [
                projectTags.DETAIL(id),
                projectTags.FAST_DETAIL(id),
                // Only invalidate lists if the update affects list view (e.g., name change)
                projectTags.FAST_LIST,
                projectTags.DETAIL_LIST,
                // Project results (getProjectResults, getProjectResultsByGas) - depend on project settings
                { type: "ProjectResults" as TagTypes, id },
                // Module results - tagged by path+uniqueId; we can't know which modules belong to this project,
                // so invalidate all to ensure consistency when project settings (e.g. climate, soil) change
                { type: "ModuleResults" as TagTypes },
            ],
        }),

        // Map data specific mutation
        uploadMapData: builder.mutation<void, { projectId: number, mapData: string | null }>({
            query: ({ projectId, mapData }: { projectId: number, mapData: string | null }) => ({
                url: `projects/${projectId}/`,
                method: "PATCH",
                body: { map_data: mapData }
            }),
            invalidatesTags: (_result, _error, { projectId }: { projectId: number, mapData: string | null }) => [
                // Only invalidate detailed view since map data isn't in summary
                projectTags.DETAIL(projectId)
            ]
        }),

        deleteProject: builder.mutation<Project, number>({
            query: (id: number) => ({
                url: `projects/${id}/`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, id: number) => [
                projectTags.DETAIL(id),
                projectTags.FAST_DETAIL(id),
                projectTags.FAST_LIST,
                projectTags.DETAIL_LIST
            ],
        }),

        finalizeProject: builder.mutation<Project, number>({
            query: (id: number) => ({
                url: `projects/${id}/`,
                method: "PATCH",
                body: {
                    is_finalized: true
                }
            }),
            invalidatesTags: [projectTags.FAST_LIST, projectTags.DETAIL_LIST],
        }),

        // Upload a new attachment
        uploadProjectAttachment: builder.mutation<ProjectAttachment, UploadAttachmentPayload>({
            query: ({ project, file }: UploadAttachmentPayload) => {
                const formData = new FormData();
                formData.append('project', project.toString());
                formData.append('file', file);

                return {
                    url: 'project-attachments/',
                    method: 'POST',
                    body: formData,
                    formData: true,
                };
            },
            invalidatesTags: [{ type: 'ProjectAttachments' as TagTypes, id: 'LIST' }],
        }),

        // Delete an attachment
        deleteProjectAttachment: builder.mutation<void, number>({
            query: (id: number) => ({
                url: `project-attachments/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, id: number) => [
                { type: 'ProjectAttachments' as TagTypes, id },
                { type: 'ProjectAttachments' as TagTypes, id: 'LIST' },
            ],
        }),

        createProjectCopy: builder.mutation<Project, number>({
            query: (id: number) => ({
                url: `projects/${id}/copy/`,
                method: "POST",
            }),
            invalidatesTags: [projectTags.FAST_LIST, projectTags.DETAIL_LIST],
        }),

        deleteMembership: builder.mutation<void, number>({
            query: (id: number) => ({
                url: `project-memberships/${id}/`,
                method: "DELETE"
            }),
            invalidatesTags: ['ProjectUsers' as TagTypes],
        }),

        addTagToProject: builder.mutation<void, { projectId: number, name: string }>({
            query: ({ projectId, name }: { projectId: number, name: string }) => ({
                url: `projects/${projectId}/tags/`,
                method: "POST",
                body: { name }
            }),
            invalidatesTags: (_result, _error, { projectId }: { projectId: number, name: string }) => [
                { type: 'ProjectTags' as TagTypes, id: projectId }, 'Tags'
            ],
        }),

        deleteTagFromProject: builder.mutation<void, { projectId: number, tagId: number }>({
            query: ({ projectId, tagId }: { projectId: number, tagId: number }) => ({
                url: `projects/${projectId}/tags/${tagId}/`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, { projectId }: { projectId: number, tagId: number }) => [
                { type: 'ProjectTags' as TagTypes, id: projectId },
                'Tags' as TagTypes
            ]
        }),

        moveToTrashbin: builder.mutation<Project, MoveToTrashbinPayload>({
            query: ({ id, restore }: MoveToTrashbinPayload) => ({
                url: `projects/${id}/`,
                method: "PATCH",
                body: {
                    is_archived: restore ? false : true
                }
            }),
            invalidatesTags: [projectTags.FAST_LIST, projectTags.DETAIL_LIST],
        }),

        togglePublicProject: builder.mutation<Project, { projectId: number, isPublic: boolean }>({
            query: ({ projectId, isPublic }: { projectId: number, isPublic: boolean }) => ({
                url: `projects/${projectId}/`,
                method: "PATCH",
                body: { is_public: isPublic }
            }),
            invalidatesTags: (_result, _error, { projectId }) => [projectTags.DETAIL(projectId)],
        }),

        toggleProjectLock: builder.mutation<ProjectLockInfo, { projectId: number, isLocked: boolean }>({
            query: ({ projectId, isLocked }: { projectId: number, isLocked: boolean }) => ({
                url: `projects/${projectId}/${isLocked ? "lock" : "unlock"}/`,
                method: "POST",
            }),
            invalidatesTags: (_result, _error, { projectId }) => [projectTags.DETAIL(projectId)],
        }),

        copyProjectAsync: builder.mutation<CopyAsyncResponse, number>({
            query: (projectId) => ({
                url: `projects/${projectId}/copy/async/`,
                method: 'POST',
            }),
            invalidatesTags: [projectTags.FAST_LIST, projectTags.DETAIL_LIST],
        }),

        triggerAsyncReport: builder.mutation<ReportAsyncResponse, ReportAsyncPayload>({
            query: ({ id, activities, template, currentLanguage }) => {
                const params = new URLSearchParams();
                if (activities) params.append('activities', activities.join(','));
                if (template) params.append('template', template);

                return {
                    url: `projects/${id}/report/async/?${params.toString()}`,
                    method: 'POST',
                    headers: currentLanguage ? { 'Accept-Language': currentLanguage } : {},
                };
            },
        }),

        exportProject: builder.query<Blob, { id: number; projectName: string }>({
            query: ({ id }) => ({
                url: `projects/${id}/export/`,
                responseHandler: (response: Response) => response.blob(),
            }),
            async onQueryStarted({ projectName }, { queryFulfilled }) {
                try {
                    const { data: blob } = await queryFulfilled;
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `${projectName}.exactp`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                } catch (error) {
                    console.error("Export failed:", error);
                }
            },
        }),

        importProject: builder.mutation<ImportProjectResponse, ImportProjectPayload>({
            query: ({ data, forceCopy }: ImportProjectPayload) => ({
                url: `projects/import_project/${forceCopy ? '?forceCopy=true' : ''}`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: [projectTags.FAST_LIST, projectTags.DETAIL_LIST],
        }),
    });

    return apiSlice.injectEndpoints({ endpoints });
} 